import React from 'react'
import { AsyncContentProgress, Button, Card, CardContent, Modal } from '@/shared/ui'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { MoreHorizontal } from 'lucide-react'
import { useUserDateFormatter } from '@/modules/auth/hooks/useUserTimezone'
import { normalizeAuthorName } from '@/shared/utils/authorName'
import { useToast } from '@/shared/ui/Toast'
import type { VisionItem } from '../services/visionService'
import { deleteVision, updateVision } from '../services/visionService'
import type { VisionCommunitySummary } from '../services/visionCommunityService'
import { VisionCommunityEntry } from './VisionCommunityEntry'
import { NewVisionModal, type NewVisionModalSubmitParams } from './NewVisionModal'
import { deleteVisionPostAttachmentsByIds } from '../services/visionPostAttachmentsService'
import { uploadAndAddVisionPostAttachments } from '../services/visionPostAttachmentsUploadService'
import {
  PostContent,
  PostAttachments,
  PostCardAuthorMeta,
  PostPreviewCard,
} from '@/modules/post-shared'
import { buildLinkPreview, extractFirstUrlFromHtml } from '@/modules/comunidade/services/linkPreviewService'
import { CommunityLinkPreviewCard } from '@/modules/comunidade/components/CommunityLinkPreviewCard'
import type { CommunitySummary } from '@/modules/comunidade/services/communityService'
import { OpportunityCommunityEntry } from '@/modules/comunidade/components/OpportunityCommunityEntry'
import type { OpportunityItem } from '@/modules/oportunidades/types'
import { OpportunityCompactCard } from '@/modules/oportunidades/components/OpportunityCompactCard'
import { VisionTimelineHistory } from './VisionTimelineHistory'

type VisionCardProps = {
  vision: VisionItem
  communityEnabled: boolean
  currentUserId: string | null
  canDeleteOthers: boolean
  thermoAllowed: boolean
  noteForMaster?: string
  linkedOpportunity?: OpportunityItem | null
  visionCommunitySummary?: VisionCommunitySummary | null
  opportunityCommunitySummary?: CommunitySummary | null
  focusMessageId?: number | null
  timelineRootId?: number | null
  showTimelineOverlay?: boolean
  onChanged?: () => void
}

export function VisionCard({
  vision,
  communityEnabled,
  currentUserId,
  canDeleteOthers,
  thermoAllowed,
  noteForMaster,
  linkedOpportunity,
  visionCommunitySummary,
  opportunityCommunitySummary,
  focusMessageId,
  timelineRootId,
  showTimelineOverlay,
  onChanged,
}: VisionCardProps) {
  const { formatUserDateTime } = useUserDateFormatter()
  const { add: toast } = useToast()
  const displayAuthorName = normalizeAuthorName(vision.authorName)

  const [editOpen, setEditOpen] = React.useState(false)
  const [deleteOpen, setDeleteOpen] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const linkPreview = React.useMemo(() => {
    const url = extractFirstUrlFromHtml(String(vision.contentHtml || ''))
    if (!url) return null
    return buildLinkPreview(url)
  }, [vision.contentHtml])

  const isAuthor =
    currentUserId && vision.authorId && String(vision.authorId) === String(currentUserId)
  const canEdit = !!isAuthor
  const canDelete = !!isAuthor || canDeleteOthers
  const canManage = canEdit || canDelete

  async function handleEdit(params: NewVisionModalSubmitParams) {
    await updateVision(vision.id, {
      title: params.title,
      html: params.html,
      linkedOpportunityId: params.linkedOpportunityId,
      linkedTimelineRootId: params.linkedTimelineRootId,
    })

    if (params.removeAttachmentIds.length > 0) {
      await deleteVisionPostAttachmentsByIds(params.removeAttachmentIds)
    }

    if (params.newAttachments.length > 0) {
      const res = await uploadAndAddVisionPostAttachments(vision.id, params.newAttachments)
      if (res.failedCount > 0) {
        toast({
          title: 'Alguns anexos falharam',
          description: `Anexos enviados: ${res.uploadedCount}. Falhas: ${res.failedCount}.`,
          variant: 'warning',
          soft: true,
        })
      }
    }

    if (onChanged) onChanged()
  }

  async function handleConfirmDelete() {
    if (deleting) return
    setDeleting(true)
    try {
      await deleteVision(vision.id)
      if (onChanged) onChanged()
      setDeleteOpen(false)
    } catch (e: any) {
      toast({
        title: 'Falha ao excluir',
        description: e?.message || 'Tente novamente em instantes.',
        variant: 'error',
      })
    } finally {
      setDeleting(false)
    }
  }

  const menuItems = [
    canEdit
      ? {
          label: 'Editar',
          onSelect: () => setEditOpen(true),
        }
      : null,
    canDelete
      ? {
          label: 'Excluir',
          onSelect: () => setDeleteOpen(true),
        }
      : null,
  ].filter(Boolean) as { label: string; onSelect: () => void }[]

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold leading-tight text-foreground">
              {vision.title}
            </div>
          </div>
          {canManage && menuItems.length > 0 && (
            <div className="ml-2 flex items-start">
              <DropdownMenu
                align="end"
                trigger={(
                  <button
                    type="button"
                    className="inline-flex items-center rounded p-1 hover:bg-muted/30"
                  >
                    <MoreHorizontal className="h-5 w-5 text-[#434343]" />
                  </button>
                )}
                items={menuItems}
              />
            </div>
          )}
        </div>

        <div className="mt-3 min-w-0 space-y-3">
          <PostContent html={vision.contentHtml} />

          <PostAttachments attachments={vision.attachments} />

          {linkPreview && (
            <div>
              <CommunityLinkPreviewCard preview={linkPreview} />
            </div>
          )}

          {vision.linkedOpportunityId !== null ? (
            linkedOpportunity ? (
              <OpportunityCompactCard
                item={linkedOpportunity}
                thermoAllowed={thermoAllowed}
                noteForMaster={noteForMaster}
                interactive={false}
              />
            ) : (
              <div className="rounded-xl bg-background/20 p-3">
                <AsyncContentProgress loading label="Carregando oportunidade...">
                  {null}
                </AsyncContentProgress>
              </div>
            )
          ) : null}
        </div>

        {(() => {
          const authorMeta = (
            <PostCardAuthorMeta
              name={displayAuthorName}
              avatarUrl={vision.authorAvatarUrl}
              dateLabel={vision.createdAt ? formatUserDateTime(vision.createdAt) : null}
            />
          )

          if (!communityEnabled) {
            return <div className="mt-3 flex justify-end">{authorMeta}</div>
          }

          if (vision.linkedOpportunityId !== null) {
            const oid = vision.linkedOpportunityId
            const symbol = linkedOpportunity?.symbol ?? String(oid)

            return (
              <div className="mt-3">
                <OpportunityCommunityEntry
                  opportunityId={oid}
                  symbol={symbol}
                  name={linkedOpportunity?.name ?? null}
                  logoUrl={linkedOpportunity?.logo_url ?? null}
                  enabled={communityEnabled}
                  summary={opportunityCommunitySummary ?? null}
                  overlayContent={(
                    <PostPreviewCard
                      title={vision.title}
                      contentHtml={vision.contentHtml}
                      attachments={vision.attachments}
                      lightboxVariant="drawer"
                      footer={
                        linkedOpportunity ? (
                          <OpportunityCompactCard
                            item={linkedOpportunity}
                            thermoAllowed={thermoAllowed}
                            noteForMaster={noteForMaster}
                            interactive={false}
                          />
                        ) : (
                          <div className="rounded-xl bg-background/20 p-3">
                            <AsyncContentProgress loading label="Carregando oportunidade...">
                              {null}
                            </AsyncContentProgress>
                          </div>
                        )
                      }
                    />
                  )}
                  renderFooter={(summaryRow) => (
                    <div className="mt-2 flex w-full items-start justify-between gap-2">
                      <div className="min-w-0 flex flex-1 -mt-2">{summaryRow}</div>
                      <div className="flex flex-none items-start justify-end">{authorMeta}</div>
                    </div>
                  )}
                />
              </div>
            )
          }

          return (
            <VisionCommunityEntry
              visionId={vision.id}
              threadId={timelineRootId ?? vision.id}
              title={vision.title}
              contentHtml={vision.contentHtml}
              attachments={vision.attachments}
              enabled={communityEnabled}
              summary={visionCommunitySummary}
              focusMessageId={focusMessageId}
              footerRight={authorMeta}
              overlayContent={
                showTimelineOverlay
                  ? (
                      <VisionTimelineHistory
                        timelineRootId={timelineRootId ?? vision.id}
                      />
                    )
                  : undefined
              }
            />
          )
        })()}

        {canEdit && (
          <NewVisionModal
            open={editOpen}
            onOpenChange={setEditOpen}
            mode="edit"
            visionId={vision.id}
            initialTitle={vision.title}
            initialHtml={vision.contentHtml}
            initialAttachments={vision.attachments}
            initialLinkedOpportunityId={vision.linkedOpportunityId}
            initialLinkedTimelineRootId={vision.linkedTimelineRootId}
            onSubmit={handleEdit}
          />
        )}
        <Modal
          open={deleteOpen}
          onOpenChange={(v) => {
            if (!v && !deleting) setDeleteOpen(false)
          }}
          dismissible={!deleting}
          className="max-w-md"
        >
          <div className="space-y-4">
            <div className="text-lg font-semibold">Excluir visão</div>
            <p className="text-sm text-foreground/80">
              Tem certeza que deseja excluir esta visão?
            </p>
            <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-foreground/80">
              Essa exclusão não poderá ser desfeita.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting}>
                {deleting ? 'Excluindo...' : 'Excluir'}
              </Button>
            </div>
          </div>
        </Modal>
      </CardContent>
    </Card>
  )
}
